import { useState, useEffect, useRef } from "react";
import { Menu, X } from "lucide-react";
import logo from "@/assets/1-wood-floors-logo.webp";
import QuoteModal from "@/components/QuoteModal";
import { featuredProjects } from "@/data/featuredProjects";

const serviceAreaGroups = [
  {
    header: "Metro Detroit (Primary)",
    cities: [
      { text: "Northville", href: "/hardwood-flooring/northville-mi" },
      { text: "Livonia", href: "/" },
      { text: "Plymouth", href: "/hardwood-flooring/plymouth-mi" },
      { text: "Birmingham", href: "/hardwood-flooring/birmingham-mi" },
    ],
  },
  {
    header: "Metro Detroit (Extended)",
    cities: [
      { text: "Bloomfield Hills", href: "/hardwood-flooring/bloomfield-hills-mi" },
      { text: "Troy", href: "/hardwood-flooring/troy-mi" },
      { text: "West Bloomfield", href: "/hardwood-flooring/west-bloomfield-mi" },
      { text: "Farmington Hills", href: "/hardwood-flooring/farmington-hills-mi" },
      { text: "Novi", href: "/hardwood-flooring/novi-mi" },
      { text: "Canton", href: "/hardwood-flooring/canton-mi" },
      { text: "Rochester Hills", href: "/hardwood-flooring/rochester-hills-mi" },
      { text: "Dearborn", href: "/hardwood-flooring/dearborn-mi" },
      { text: "Grosse Pointe", href: "/hardwood-flooring/grosse-pointe-mi" },
      { text: "Orchard Lake", href: "/hardwood-flooring/orchard-lake-mi" },
      { text: "Wixom", href: "/hardwood-flooring/wixom-mi" },
    ],
  },
  {
    header: "Ann Arbor Corridor",
    cities: [
      { text: "Ann Arbor", href: "/hardwood-flooring/ann-arbor-mi" },
      { text: "Brighton", href: "/hardwood-flooring/brighton-mi" },
      { text: "Dexter", href: "/hardwood-flooring/dexter-mi" },
      { text: "Saline", href: "/hardwood-flooring/saline-mi" },
      { text: "Ypsilanti", href: "/hardwood-flooring/ypsilanti-mi" },
      { text: "Chelsea", href: "/hardwood-flooring/chelsea-mi" },
      { text: "South Lyon", href: "/hardwood-flooring/south-lyon-mi" },
      { text: "Pinckney", href: "/hardwood-flooring/pinckney-mi" },
    ],
  },
  {
    header: "Northern Michigan",
    cities: [
      { text: "Harbor Springs", href: "/hardwood-flooring/harbor-springs-mi" },
      { text: "Petoskey", href: "/hardwood-flooring/petoskey-mi" },
      { text: "Traverse City", href: "/hardwood-flooring/traverse-city-mi" },
      { text: "Charlevoix", href: "/hardwood-flooring/charlevoix-mi" },
      { text: "Bay Harbor", href: "/hardwood-flooring/bay-harbor-mi" },
      { text: "Bellaire", href: "/hardwood-flooring/bellaire-mi" },
      { text: "Leelanau", href: "/hardwood-flooring/leelanau-mi" },
    ],
  },
];

const navItems = [
  { label: "Home", href: "/" },
  {
    label: "Services",
    sections: [
      {
        links: [
          { text: "Hardwood Floor Installation", href: "/installation" },
          { text: "New Site Finished Floors", href: "/sand-and-finish" },
          { text: "Older Floor Restoration", href: "/floor-restoration" },
          { text: "LVP Installation", href: "/lvp-installation" },
          { text: "Waterproof Laminate Installation", href: "/laminate-installation" },
          { text: "Alterna Engineered Tile Installation", href: "/alterna-tile-installation" },
          { text: "Carpet & Stair Runners", href: "/dreamweaver-carpet" },
          { text: "Advanced Dust Control Refinishing", href: "/refinishing" },
          { text: "NWFA Inspection", href: "/inspections" },
          { text: "Subfloor Leveling & Repair", href: "/subfloor-leveling" },
          { text: "Staircase Renovations", href: "/staircase-renovations" },
          { text: "Commercial Projects", href: "/commercial" },
          { text: "Product Specification", href: "/product-specification" },
        ],
      },
    ],
  },
  {
    label: "Shop by Brand",
    sections: [
      {
        links: [
          { text: "Michigan Plank Flooring: Custom Wide-Plank European White Oak", href: "/michigan-plank-flooring" },
          { text: "Lauzon: Hardwood Flooring (Solid or Engineered)", href: "/brands/lauzon/" },
          { text: "Viking: European Hardwood (Engineered Wide Plank White Oak)", href: "/brands/viking" },
          { text: "Mercier: Premium Solid & Engineered Hardwood", href: "/brands/mercier" },
          { text: "Olde Wood Limited: Reclaimed Hardwood & Beams", href: "/brands/olde-wood" },
          { text: "Indusparquet: Exotic Species Hardwood", href: "/brands/indusparquet" },
          { text: "Cali Floors: Luxury Vinyl Floors (LVP)", href: "/brands/cali/" },
          { text: "Quick-Step: Waterproof Laminate", href: "/brands/quick-step" },
          { text: "Bella Cera: Hand-Scraped European Oak", href: "/brands/bella-cera" },
          { text: "Bamboo Flooring: High Density Strand Woven", href: "/brands/ironwood" },
          { text: "Armstrong Alterna: Luxury Vinyl Tile (LVT)", href: "/brands/alterna" },
          { text: "Dreamweaver Carpet: Whole-Home Carpet & Stair Runners", href: "/dreamweaver-carpet" },
        ],
      },
    ],
    dropWidth: "min-w-[320px]",
  },
  {
    label: "Flooring",
    sections: [
      {
        links: [
          { text: "Hardwood Flooring", href: "/hardwood-flooring" },
          { text: "Luxury Vinyl Plank Flooring", href: "/luxury-vinyl-plank-flooring" },
          { text: "Laminate Flooring", href: "/laminate-flooring" },
          { text: "Reclaimed Wood Flooring", href: "/reclaimed-barnwood" },
        ],
      },
    ],
  },
  
  {
    label: "Service Areas",
    isServiceAreas: true,
  },
  {
    label: "Projects",
    sections: [
      { title: "Featured Homes", links: featuredProjects },
      { links: [{ text: "All Projects", href: "/projects" }] },
    ],
    dropWidth: "min-w-[280px]",
  },
  { label: "Water Damage", href: "/water-damage-flooring" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

const SiteHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openNav, setOpenNav] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const [menuTop, setMenuTop] = useState(64);

  useEffect(() => {
    if (!mobileOpen) return;
    const update = () => setMenuTop(headerRef.current?.getBoundingClientRect().bottom ?? 64);
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    window.visualViewport?.addEventListener("resize", update);
    const observer = new ResizeObserver(update);
    if (headerRef.current) observer.observe(headerRef.current);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
      window.visualViewport?.removeEventListener("resize", update);
      observer.disconnect();
    };
  }, [mobileOpen]);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileExpanded(null);
  };

  return (
    <>
      <header
        ref={headerRef}
        className={`sticky top-0 z-[900] h-16 md:h-[72px] flex items-center justify-between px-4 md:px-8 border-b border-foreground/5 backdrop-blur-2xl backdrop-saturate-[1.8] bg-background/[.78] transition-shadow duration-300 ${scrolled ? "shadow-lg shadow-foreground/5" : ""}`}
      >
        <a href="/" className="flex items-center gap-2.5">
          <img loading="lazy" width="227" height="154" src={logo} alt="1 Wood Floors  Livonia MI" className="h-11 md:h-[52px] w-auto drop-shadow-[0_1px_6px_rgba(154,108,40,0.35)]" />
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-0">
          {navItems.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => setOpenNav(item.label)}
              onMouseLeave={() => setOpenNav(null)}
            >
              {item.href ? (
                <a href={item.href} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">
                  {item.label}
                </a>
              ) : (
                <button className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap bg-transparent border-none">
                  {item.label}
                  <span className={`text-[7px] opacity-40 transition-transform duration-200 ${openNav === item.label ? "rotate-180" : ""}`}>▾</span>
                </button>
              )}

              {/* Service Areas 4-column mega menu */}
              {(item as any).isServiceAreas && openNav === item.label && (
                <div className="absolute top-full right-0 pt-2 z-[1000]">
                  <div className="bg-background/[.96] backdrop-blur-[40px] border border-border rounded-[20px] shadow-xl py-4 px-5 grid grid-cols-4 gap-5 min-w-[720px]">
                    {serviceAreaGroups.map((group) => (
                      <div key={group.header}>
                        <span className="block text-[12px] font-bold text-foreground leading-tight mb-2">
                          {group.header}
                        </span>
                        {group.cities.map((city) => (
                          <a
                            key={city.text}
                            href={city.href}
                            className="block py-[4px] text-[12px] text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {city.text}
                          </a>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Standard sectioned dropdown */}
              {item.sections && openNav === item.label && (
                <div className={`absolute top-full left-0 pt-2 z-[1000]`}>
                <div className={`bg-background/[.96] backdrop-blur-[40px] border border-border rounded-[20px] shadow-xl ${(item as any).dropWidth || "min-w-[220px]"} py-2 overflow-hidden`}>
                  {item.sections.map((section, si) => (
                    <div key={si}>
                      {(section as any).title && (
                        <div className={`px-[18px] py-[6px] text-[9.5px] font-bold tracking-[.14em] uppercase text-muted-foreground ${si > 0 ? "border-t border-border pt-[8px]" : ""}`}>
                          {(section as any).title}
                        </div>
                      )}
                      {section.links.map((link) => (
                        <a key={link.text} href={link.href} className="flex items-center gap-2 px-[18px] py-[9px] text-[13px] text-muted-foreground hover:bg-accent hover:text-foreground hover:pl-[22px] transition-all">
                          {link.text}
                        </a>
                      ))}
                    </div>
                  ))}
                </div>
                </div>
              )}
            </div>
          ))}
          <QuoteModal
            trigger={
              <button className="ml-3 bg-foreground text-primary-foreground px-[18px] py-[7px] rounded-full text-xs font-medium hover:bg-primary transition-colors whitespace-nowrap">
                Schedule a Call
              </button>
            }
          />
        </nav>

        {/* Mobile controls */}
        <div className="flex lg:hidden items-center gap-3">
          <a href="tel:2483258853" className="bg-primary text-accent-foreground px-3 py-1.5 rounded-full text-xs font-medium">
            Call Us
          </a>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="bg-transparent border-none text-foreground p-1"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile menu overlay */}
      {mobileOpen && (
        <div style={{ top: menuTop }} className="fixed inset-x-0 bottom-0 z-[899] bg-background overflow-y-auto lg:hidden">
          <nav className="px-5 py-6 flex flex-col gap-1">
            {navItems.map((item) => (
              <div key={item.label}>
                {item.href ? (
                  <a
                    href={item.href}
                    onClick={closeMobile}
                    className="block py-3 px-3 text-base font-medium text-foreground border-b border-border/50"
                  >
                    {item.label}
                  </a>
                ) : (
                  <>
                    <button
                      onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                      className="w-full flex justify-between items-center py-3 px-3 text-base font-medium text-foreground border-b border-border/50 bg-transparent border-x-0 border-t-0"
                    >
                      {item.label}
                      <span className={`text-xs text-muted-foreground transition-transform duration-200 ${mobileExpanded === item.label ? "rotate-180" : ""}`}>▾</span>
                    </button>
                    {mobileExpanded === item.label && (
                      <div className="bg-accent rounded-xl mb-2 py-3 px-3">
                        {(item as any).isServiceAreas ? (
                          <div className="flex flex-col gap-4">
                            {serviceAreaGroups.map((group) => (
                              <div key={group.header}>
                                <span className="block text-sm font-semibold text-foreground mb-1">
                                  {group.header}
                                </span>
                                {group.cities.map((city) => (
                                  <a key={city.text} href={city.href} onClick={closeMobile} className="block py-1 text-sm text-muted-foreground">
                                    {city.text}
                                  </a>
                                ))}
                              </div>
                            ))}
                          </div>
                        ) : (
                          item.sections?.map((section, si) => (
                            <div key={si} className="mb-3 last:mb-0">
                              {(section as any).title && <p className="text-[10px] font-bold tracking-[.14em] uppercase text-muted-foreground mb-2">{(section as any).title}</p>}
                              {section.links.map((link) => (
                                <a key={link.text} href={link.href} onClick={closeMobile} className="block py-1.5 text-sm text-muted-foreground">
                                  {link.text}
                                </a>
                              ))}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
            <QuoteModal
              trigger={
                <button
                  onClick={closeMobile}
                  className="mt-4 bg-foreground text-background px-6 py-3.5 rounded-full text-sm font-medium text-center w-full"
                >
                  Schedule a Call
                </button>
              }
            />
          </nav>
        </div>
      )}
    </>
  );
};

export default SiteHeader;